import {InMemoryCache, QueryOptions} from '@apollo/client/core';
import {dataIdFromObject} from 'app/core/apollo/data-id-from-object';
import {FragmentHelper} from 'app/shared/modules/api/helpers/fragment-helper';
import {environment} from 'src/environments/environment';
import {cloneDeep, isObject} from 'app/core/helpers/object';
import {LocalStorageService} from 'app/core/services/local-storage.service';
import {AuthServiceHelper} from 'app/core/services/auth.service.helper';
import {ApiLocalCache} from 'app/shared/modules/api/helpers/api-local-cache';

export const deepRemoveEntityFromCache = (cacheSlice, id) => {
    const walk = slice => {
        if (Array.isArray(slice)) {
            return slice.filter(item => item.id !== id);
        }
        if (isObject(slice)) {
            return Object.keys(slice).reduce(
                (acc, key) => {
                    const value = acc[key];
                    if (key === id) {
                        delete acc[key];
                    } else if (isObject(value)) {
                        acc[key] = walk(value);
                    }
                    return acc;
                },
                {...slice}
            );
        }
        return slice;
    };

    return walk(cacheSlice);
};

export const removeEntityById = (id: string, proxy: InMemoryCache) => {
    if (!id) {
        /* istanbul ignore next */
        if (!environment.production) {
            console.warn('[removeEntityById] empty id given');
        }
        return;
    }
    proxy.performTransaction(() => {
        let cache = {
            ...proxy.extract(),
        };
        delete cache[id];
        cache = deepRemoveEntityFromCache(cache, id);
        proxy.restore(cache);
    });
};

/**
 * @param entity
 */
export const removeEntity = entity => (proxy: InMemoryCache) => {
    removeEntityById(dataIdFromObject(entity), proxy);
};

export class ApiCacheHelper {
    private localCache = new ApiLocalCache(this.localStorage, this.auth);

    constructor(
        private fragmentHelper: FragmentHelper,
        private localStorage: LocalStorageService,
        private auth: AuthServiceHelper
    ) {}

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeEntity<T>(entity): any {
        return (proxy: InMemoryCache) => {
            removeEntityById(dataIdFromObject(entity), proxy);
        };
    }

    updateQuery<T, R>(queryOptions, processDataFn: (cache: T, data: R) => T) {
        return (proxy: InMemoryCache, {data}) => {
            const options: QueryOptions<T> = this.fragmentHelper.injectKnownFragments(queryOptions, 'query');
            const cachedData = <T>proxy.readQuery(options);
            const newData = processDataFn(cloneDeep(cachedData), <R>cloneDeep(data));
            proxy.writeQuery({...options, data: newData});
        };
    }

    cacheHours(queryOptions, hoursToKeep: number, result: unknown) {
        return this.localCache.cacheHours(queryOptions, hoursToKeep, result);
    }

    getFromCache(queryOptions): unknown {
        return this.localCache.getFromCache(queryOptions);
    }
}
